import { useState } from "react"
import type { CSSProperties } from "react"
import CardPeek from "@/components/CardFace/CardPeek"
import CardThumb from "@/components/CardFace/CardThumb"
import EmptyState from "@/components/EmptyState/EmptyState"
import JobEditModal from "@/components/JobEditModal/JobEditModal"
import StatPanel from "@/components/StatPanel/StatPanel"
import StatusDot from "@/components/StatusDot/StatusDot"
import { cardTint } from "@/components/CardFace/cardTint"
import { cardFace } from "@/content/cards"
import { DEEP_DIVE_FIELDS } from "@/content/deepDive"
import { JOB_COPY } from "@/content/job"
import { bucketWeekRange, MOSCOW_COPY, PLAN_BUCKETS, TIMELINE_WEEKS } from "@/content/priorities"
import { WRAP_COPY } from "@/content/wrap"
import { useExpiryTick } from "@/hooks/useExpiryTick"
import { useSessionStore } from "@/store/sessionStore"
import {
  jobSentence,
  selectIsFacilitator,
  selectJobs,
  selectPains,
  selectSession,
} from "@/store/selectors"
import { downloadMarkdown, reportMarkdown } from "@/utils/reportMarkdown"
import { isExpired, sessionState } from "@/utils/sessionLifecycle"
import type { Job, MoscowBucket } from "@/types"
import styles from "@/styles/pages/Debrief.module.css"

/*
 * The debrief.
 *
 * Derived from the job documents on every render, never stored. That is why
 * re-opening a finished session shows the same read, and why the Markdown export
 * and this sheet cannot drift — they are the same data through two renderers.
 * (The "Generating" beat between the job gate and this screen computes nothing;
 * see `GeneratingScreen`, which says so at length.)
 *
 * It reads EVERY job, not the current one. A session that worked three problems
 * leaves behind three sections; reading only `session.currentJobId` would
 * silently drop everything before the last job gate.
 *
 * One component, two ways in: as the `wrap` round inside a session, where the
 * floating bar carries Back and Finish & save, and at `/reports/:sessionId`,
 * where the page around it carries the breadcrumb and the date. The sheet itself
 * is identical, because it is the same document.
 */

const DATE_FORMAT: Intl.DateTimeFormatOptions = {
  day: "numeric",
  month: "long",
  year: "numeric",
}

interface DebriefProps {
  /** The report on its own drops the round heading; the page supplies one. */
  standalone?: boolean
}

export default function Debrief({ standalone = false }: DebriefProps) {
  const session = useSessionStore(selectSession)
  const jobs = useSessionStore(selectJobs)
  const pains = useSessionStore(selectPains)
  const isFacilitator = useSessionStore(selectIsFacilitator)
  const now = useExpiryTick([session?.expiresAt ?? null])
  const [exportOpen, setExportOpen] = useState(false)
  const [editingJobId, setEditingJobId] = useState<string | null>(null)

  if (!session) return null

  /*
   * Editing a job from the debrief is the facilitator's, and only while the
   * session is still live — the in-session Wrap. A completed session is a record:
   * `firestore.rules` refuses job writes once `status` is completed, so the button
   * is withheld on the standalone report rather than offering a write that fails.
   */
  const canEdit = isFacilitator && session.status !== "completed" && !isExpired(session, now)

  const worked = jobs.filter((job) => job.cardIds.length > 0)
  const userStory = session.config.userStoryEnabled
  const editingJob = editingJobId ? (worked.find((j) => j.id === editingJobId) ?? null) : null
  const painText = (id: string) => pains.find((pain) => pain.id === id)?.text ?? null

  const countIn = (job: Job, bucket: string) =>
    job.cardIds.filter((id) => job.placements[id]?.bucket === bucket).length

  /* Not every filed card is an opportunity — a Won't have is the room saying no
     to one, so it does not belong in a count the customer reads as "what we
     found". */
  const opportunities = worked.reduce(
    (total, job) =>
      total + job.cardIds.filter((id) => job.placements[id]?.bucket !== "wont").length,
    0
  )
  const buildReady = worked.reduce((total, job) => total + countIn(job, "must"), 0)

  const stats = [
    { value: String(opportunities), label: WRAP_COPY.statOpportunities(opportunities) },
    { value: String(buildReady), label: WRAP_COPY.statBuildReady },
    { value: WRAP_COPY.weeks(MOSCOW_COPY.must.weeks), label: WRAP_COPY.statFirstValue },
  ]

  const generatedOn = new Date().toLocaleDateString("en-GB", DATE_FORMAT)

  const download = () => {
    setExportOpen(false)
    const slug = (session.customerName || session.name || "session")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "")
    downloadMarkdown(
      `${slug}-ai-opportunity-read.md`,
      reportMarkdown({ session, jobs, pains, generatedOn })
    )
  }

  if (worked.length === 0) {
    return (
      <div className={styles.debrief}>
        <EmptyState title={WRAP_COPY.noJobs} />
      </div>
    )
  }

  /* The hero reads off the FIRST worked job. A session with three problems still
     has one thing it is mainly about, and stacking three hero statements would
     make the sheet open on a list rather than on a sentence. Every job gets its
     own section below. */
  const lead = worked[0]
  const leadSentence = jobSentence(lead, userStory)
  const leadPain = lead.painIds.map(painText).find((text): text is string => text !== null)

  return (
    <div className={styles.debrief} id="report-sheet">
      {editingJob ? (
        <JobEditModal
          sessionId={session.id}
          job={editingJob}
          userStoryEnabled={userStory}
          onClose={() => setEditingJobId(null)}
        />
      ) : null}
      {/* The export menu belongs to the sheet in both modes: the standalone page
          has no round controls to put it in, and the round's bar is already
          full. What sits beside it differs — the round is dated by the sheet,
          the standalone report by its own state. */}
      <div className={styles.roundHead}>
        {/* Left of the date: which customer this sheet belongs to, so a
            facilitator paging between debriefs is never reading one without
            knowing whose it is. The standalone page already carries this in
            its own breadcrumb, so it only renders here, inside the round. */}
        {standalone ? null : (
          <p className={styles.roundCrumb}>
            {WRAP_COPY.crumb} /{" "}
            <span className={styles.roundCrumbCustomer}>
              {WRAP_COPY.reportTitle(session.customerName || session.name)}
            </span>
          </p>
        )}
        <span className={styles.roundHeadRight}>
          {standalone ? (
            <span className={styles.roundState}>
              <StatusDot state={sessionState(session)} />
              <span className={styles.roundMeta}>{generatedOn}</span>
            </span>
          ) : (
            <p className={styles.roundMeta}>{WRAP_COPY.meta(generatedOn)}</p>
          )}
          <ExportMenu open={exportOpen} onToggle={setExportOpen} onDownload={download} />
        </span>
      </div>

      <section className={styles.hero}>
        <div className={styles.heroText}>
          <p className={styles.eyebrow}>
            <span className={styles.asterisk} aria-hidden="true">
              ✳
            </span>
            {WRAP_COPY.opportunity}
          </p>
          {leadSentence ? (
            <div className={styles.heroSentenceRow}>
              <h2 className={styles.heroSentence}>{leadSentence}</h2>
              {canEdit ? (
                <button
                  type="button"
                  className={styles.editJob}
                  onClick={() => setEditingJobId(lead.id)}
                >
                  {JOB_COPY.editAction}
                </button>
              ) : null}
            </div>
          ) : null}
          {leadPain ? <p className={styles.heroPain}>“{leadPain}”</p> : null}
        </div>
        <div className={styles.heroStats}>
          <StatPanel stats={stats} />
        </div>
      </section>

      {worked.map((job, index) => {
        const sentence = jobSentence(job, userStory)
        const from = job.painIds.map(painText).filter((text): text is string => text !== null)
        const idsIn = (bucket: MoscowBucket) =>
          job.cardIds.filter((id) => job.placements[id]?.bucket === bucket)

        const buildEntries = PLAN_BUCKETS.flatMap((bucket) =>
          idsIn(bucket).map((id) => ({ id, bucket }))
        )
        const mustEntries = buildEntries.filter((entry) => entry.bucket === "must")
        const laterEntries = buildEntries.filter((entry) => entry.bucket !== "must")
        const raised = idsIn("wont")
        const dived = job.cardIds.filter((id) => {
          const detail = job.deepDive[id]
          return detail && DEEP_DIVE_FIELDS.some((field) => detail[field.key].trim())
        })

        const buildCard = (entry: (typeof buildEntries)[number], position: number) => {
          const { id, bucket } = entry
          const face = cardFace(id)
          const tint = cardTint(id)
          const note = job.placements[id]?.note.trim()
          const number = String(position + 1).padStart(2, "0")
          const primary = bucket === "must"
          /* A Could have never got a committed week — see `MOSCOW_COPY.could.when`
             — so its badge reads "Later" instead of a range nobody agreed to. */
          const weekBadge = (() => {
            if (bucket === "could") return WRAP_COPY.later
            const { from, to } = bucketWeekRange(bucket)
            return WRAP_COPY.weekRange(from, to)
          })()

          return (
            <li
              key={id}
              className={
                primary
                  ? styles.buildCard
                  : bucket === "could"
                    ? `${styles.buildCardSecondary} ${styles.buildCardLater}`
                    : styles.buildCardSecondary
              }
            >
              <div className={styles.buildCardHead}>
                {primary ? (
                  <span className={styles.buildNumber}>{number}</span>
                ) : (
                  <span className={styles.buildTag}>
                    {number} · {MOSCOW_COPY[bucket].label}
                  </span>
                )}
                <span className={primary ? styles.buildWeekCommitted : styles.buildWeek}>
                  {weekBadge}
                </span>
              </div>
              {primary ? (
                <span className={styles.buildEyebrow}>
                  <span className={styles.buildDot} style={{ background: tint.bloom }} />
                  {face?.name}
                </span>
              ) : null}
              <p className={styles.buildStatement}>{face?.statement}</p>
              {face?.scope ? <p className={styles.buildScope}>{face.scope}</p> : null}
              {note ? (
                <aside className={styles.buildWhy}>
                  <span className={styles.whyLabel}>{WRAP_COPY.whyItBelongs}</span>
                  {note}
                </aside>
              ) : null}
            </li>
          )
        }

        return (
          <section key={job.id} className={styles.job}>
            {/* The lead job's statement is already the hero, so it is not
                repeated; a second and third problem announce themselves. */}
            {worked.length > 1 ? (
              <header className={styles.jobHead}>
                <p className={styles.jobLabel}>{WRAP_COPY.jobLabel(index + 1)}</p>
                {index > 0 && sentence ? (
                  <div className={styles.jobSentenceRow}>
                    <h3 className={styles.jobSentence}>{sentence}</h3>
                    {canEdit ? (
                      <button
                        type="button"
                        className={styles.editJob}
                        onClick={() => setEditingJobId(job.id)}
                      >
                        {JOB_COPY.editAction}
                      </button>
                    ) : null}
                  </div>
                ) : null}
                {index > 0
                  ? from.map((text) => (
                      <p key={text} className={styles.jobPain}>
                        “{text}”
                      </p>
                    ))
                  : null}
              </header>
            ) : null}

            {buildEntries.length > 0 ? (
              <section className={styles.timeline}>
                <header className={styles.timelineHead}>
                  <h3 className={styles.timelineTitle}>{WRAP_COPY.whatLandsWhen}</h3>
                  <span className={styles.timelineHint}>{WRAP_COPY.timelineHint}</span>
                </header>
                <div
                  className={styles.timelineGrid}
                  /* One extra column past the numbered weeks: "Later", where every
                     Could have's bar lands — see `MOSCOW_COPY.could.when`. */
                  style={{ "--weeks": TIMELINE_WEEKS + 1 } as CSSProperties}
                >
                  <div className={styles.timelineRow} aria-hidden="true">
                    <span className={styles.timelineLabelCol} />
                    {Array.from({ length: TIMELINE_WEEKS }, (_, i) => i + 1).map((week) => (
                      <span key={week} className={styles.timelineWeek}>
                        {WRAP_COPY.week(week)}
                      </span>
                    ))}
                    <span className={styles.timelineWeek}>{WRAP_COPY.later}</span>
                  </div>
                  {buildEntries.map(({ id, bucket }) => {
                    const face = cardFace(id)
                    const tint = cardTint(id)
                    /* A Could have has no committed weeks, so its bar is the one
                       trailing "Later" column rather than a range within
                       `bucketWeekRange`. */
                    const [columnStart, columnEnd] =
                      bucket === "could"
                        ? [TIMELINE_WEEKS + 2, TIMELINE_WEEKS + 3]
                        : (() => {
                            const { from, to } = bucketWeekRange(bucket)
                            return [from + 1, to + 2]
                          })()
                    return (
                      <div key={id} className={styles.timelineRow}>
                        <span className={styles.timelineLabelCol}>
                          <span className={styles.timelineDot} style={{ background: tint.bloom }} />
                          <span className={styles.timelineName}>{face?.name}</span>
                        </span>
                        <span
                          className={styles.timelineBar}
                          data-bucket={bucket}
                          style={{ gridColumn: `${columnStart} / ${columnEnd}` }}
                        >
                          {MOSCOW_COPY[bucket].label.split(" ")[0]}
                        </span>
                      </div>
                    )
                  })}
                </div>
              </section>
            ) : null}

            <section className={styles.build}>
              <header className={styles.buildHead}>
                <h3 className={styles.buildTitle}>{WRAP_COPY.buildTheseFirst}</h3>
                <span className={styles.buildHint}>
                  {MOSCOW_COPY.must.label} ·{" "}
                  {MOSCOW_COPY.must.hint.charAt(0).toLowerCase() + MOSCOW_COPY.must.hint.slice(1)}
                </span>
              </header>

              {mustEntries.length === 0 ? (
                <p className={styles.nothing}>{WRAP_COPY.nothingHere}</p>
              ) : (
                <ul className={styles.buildPrimary}>
                  {mustEntries.map((entry, i) => buildCard(entry, i))}
                </ul>
              )}

              {laterEntries.length > 0 ? (
                <ul className={styles.buildSecondary}>
                  {laterEntries.map((entry, i) => buildCard(entry, mustEntries.length + i))}
                </ul>
              ) : null}

              {raised.length > 0 ? (
                <div className={styles.raised}>
                  <header className={styles.raisedHead}>
                    <span className={styles.raisedLabel}>{WRAP_COPY.alsoRaised}</span>
                    <span className={styles.raisedHint}>{WRAP_COPY.alsoRaisedHint}</span>
                  </header>
                  <ul className={styles.raisedList}>
                    {raised.map((id) => {
                      const face = cardFace(id)
                      return (
                        <li key={id} className={styles.raisedRejected}>
                          {face?.name}
                        </li>
                      )
                    })}
                  </ul>
                </div>
              ) : null}
            </section>

            {dived.length > 0 ? (
              <section className={styles.detail}>
                <header className={styles.detailHead}>
                  <h3 className={styles.detailLabel}>{WRAP_COPY.supportingDetail}</h3>
                  <span className={styles.detailBody}>{WRAP_COPY.supportingDetailBody}</span>
                </header>

                {dived.map((id) => {
                  const face = cardFace(id)
                  const answers = job.deepDive[id]
                  if (!answers) return null
                  return (
                    <div key={id} className={styles.detailCard}>
                      <CardPeek cardId={id}>
                        <CardThumb cardId={id} size="sm" />
                      </CardPeek>
                      <div className={styles.detailText}>
                        <span className={styles.itemEyebrow}>{face?.name}</span>
                        <span className={styles.itemStatement}>{face?.statement}</span>
                        <dl className={styles.detailRows}>
                          {DEEP_DIVE_FIELDS.filter((field) => answers[field.key].trim()).map(
                            (field) => (
                              <div key={field.key} className={styles.detailRow}>
                                <dt>{field.label}</dt>
                                <dd>{answers[field.key]}</dd>
                              </div>
                            )
                          )}
                        </dl>
                      </div>
                    </div>
                  )
                })}
              </section>
            ) : null}
          </section>
        )
      })}
    </div>
  )
}

/**
 * Export, as a menu of two.
 *
 * Print rather than a PDF library: the sheet is already one column of text, and
 * `@media print` in this stylesheet isolates it. The browser's own dialog also
 * lets someone pick a printer.
 */
export function ExportMenu({
  open,
  onToggle,
  onDownload,
}: {
  open: boolean
  onToggle: (open: boolean) => void
  onDownload: () => void
}) {
  return (
    <div className={styles.exportWrap}>
      <button
        type="button"
        className={styles.exportButton}
        aria-expanded={open}
        onClick={() => onToggle(!open)}
      >
        {WRAP_COPY.export}
        <span aria-hidden="true">▾</span>
      </button>
      {open ? (
        <div className={styles.exportMenu}>
          <button type="button" className={styles.exportItem} onClick={onDownload}>
            <span>{WRAP_COPY.exportMarkdown}</span>
            <span className={styles.exportHint}>{WRAP_COPY.exportMarkdownHint}</span>
          </button>
          <button
            type="button"
            className={styles.exportItem}
            onClick={() => {
              onToggle(false)
              window.print()
            }}
          >
            <span>{WRAP_COPY.exportPdf}</span>
            <span className={styles.exportHint}>{WRAP_COPY.exportPdfHint}</span>
          </button>
        </div>
      ) : null}
    </div>
  )
}
